import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  projectsService,
  ProjectChannel,
  SeedPost,
  ChannelAssessment,
} from '../../services/projects.service'
import ChannelPickerModal from '../../components/projects/ChannelPickerModal'

type Tab = 'channels' | 'posts' | 'settings'

const STATUS_LABELS: Record<string, string> = {
  added: 'Добавлен',
  assessing: 'Анализ...',
  assessed: 'Оценён',
  warming_up: 'Прогрев',
  warm: 'Прогрет',
  seeding: 'Сидинг',
  seeded: 'Засеян',
  cooldown: 'Пауза',
}

const STATUS_COLORS: Record<string, string> = {
  added: 'bg-gray-100 text-gray-600',
  assessing: 'bg-blue-100 text-blue-700',
  assessed: 'bg-indigo-100 text-indigo-700',
  warming_up: 'bg-amber-100 text-amber-700',
  warm: 'bg-green-100 text-green-700',
  seeded: 'bg-purple-100 text-purple-700',
  cooldown: 'bg-gray-200 text-gray-500',
}

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const pId = Number(projectId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('channels')
  const [addHandle, setAddHandle] = useState('')
  const [showPicker, setShowPicker] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState<ChannelAssessment | null>(null)

  const { data: project } = useQuery({
    queryKey: ['project', pId],
    queryFn: () => projectsService.getProject(pId),
  })
  const { data: channels = [] } = useQuery({
    queryKey: ['projectChannels', pId],
    queryFn: () => projectsService.getProjectChannels(pId),
  })
  const { data: stats } = useQuery({
    queryKey: ['projectStats', pId],
    queryFn: () => projectsService.getStats(pId),
  })
  const { data: posts = [] } = useQuery({
    queryKey: ['projectPosts', pId],
    queryFn: () => projectsService.getSeedPosts(pId),
    enabled: tab === 'posts',
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['projectChannels', pId] })
    queryClient.invalidateQueries({ queryKey: ['projectStats', pId] })
    queryClient.invalidateQueries({ queryKey: ['projectPosts', pId] })
  }

  const addMutation = useMutation({
    mutationFn: (username: string) => projectsService.addChannel(pId, { username }),
    onSuccess: () => { invalidate(); setAddHandle(''); toast.success('Канал добавлен') },
    onError: () => toast.error('Канал не найден или уже в проекте'),
  })

  const bulkAddMutation = useMutation({
    mutationFn: (handles: string[]) => projectsService.bulkAddChannels(pId, handles),
    onSuccess: (r) => { invalidate(); setAddHandle(''); toast.success(`Добавлено: ${r.added}${r.not_found.length ? `, не найдено: ${r.not_found.join(', ')}` : ''}`) },
  })

  const assessMutation = useMutation({
    mutationFn: (pcId: number) => projectsService.triggerAssessment(pId, pcId),
    onSuccess: () => { invalidate(); toast.success('Анализ запущен') },
  })
  const approveAssessment = useMutation({
    mutationFn: (pcId: number) => projectsService.approveAssessment(pId, pcId),
    onSuccess: () => { invalidate(); setSelectedAssessment(null); toast.success('Одобрено') },
  })
  const rejectAssessment = useMutation({
    mutationFn: (pcId: number) => projectsService.rejectAssessment(pId, pcId),
    onSuccess: () => { invalidate(); setSelectedAssessment(null) },
  })

  const generateMutation = useMutation({
    mutationFn: (pcId: number) => projectsService.generatePost(pId, pcId),
    onSuccess: () => { invalidate(); toast.success('Генерация запущена') },
  })
  const approvePostMutation = useMutation({
    mutationFn: (postId: number) => projectsService.updatePost(pId, postId, { status: 'approved' }),
    onSuccess: () => { invalidate(); toast.success('Пост одобрен') },
  })
  const sendPostMutation = useMutation({
    mutationFn: (postId: number) => projectsService.sendPost(pId, postId),
    onSuccess: () => { invalidate(); toast.success('Пост отправлен') },
  })

  const removeMutation = useMutation({
    mutationFn: (pcId: number) => projectsService.removeChannel(pId, pcId),
    onSuccess: () => invalidate(),
  })

  const viewAssessment = async (pcId: number) => {
    try {
      const a = await projectsService.getAssessment(pId, pcId)
      setSelectedAssessment(a)
    } catch {
      toast.error('Нет оценки')
    }
  }

  const handleAddSubmit = () => {
    const handles = addHandle.split(/[\n,]+/).map(h => h.trim().replace('@', '')).filter(Boolean)
    if (handles.length === 1) {
      addMutation.mutate(handles[0])
    } else if (handles.length > 1) {
      bulkAddMutation.mutate(handles)
    }
  }

  if (!project) return <div className="text-gray-500 p-6">Загрузка...</div>

  const tabs: { key: Tab; label: string }[] = [
    { key: 'channels', label: `Каналы (${stats?.total_channels || 0})` },
    { key: 'posts', label: `Посты (${stats?.total_seed_posts || 0})` },
    { key: 'settings', label: 'Настройки' },
  ]

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/projects')} className="text-sm text-gray-400 hover:text-gray-600 mb-1">
            ← Проекты
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
          {project.description && <p className="text-sm text-gray-500">{project.description}</p>}
        </div>
      </div>

      {/* Funnel stats */}
      {stats && (
        <div className="flex gap-2 flex-wrap">
          {(['added', 'assessing', 'assessed', 'warming_up', 'warm', 'seeded', 'cooldown'] as const).map(s => {
            const count = stats[s]
            if (!count) return null
            return (
              <span key={s} className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[s] || 'bg-gray-100'}`}>
                {STATUS_LABELS[s]}: {count}
              </span>
            )
          })}
          {stats.total_leads > 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Лидов: {stats.total_leads}
            </span>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Channels */}
      {tab === 'channels' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setShowPicker(true)}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
            >
              + Выбрать из моих каналов
            </button>
            <div className="flex-1 flex gap-2">
              <input
                value={addHandle}
                onChange={e => setAddHandle(e.target.value)}
                placeholder="Или @handle (через запятую для bulk)"
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
                onKeyDown={e => e.key === 'Enter' && handleAddSubmit()}
              />
              <button onClick={handleAddSubmit} className="px-3 py-2 border text-gray-700 text-sm rounded-lg hover:bg-gray-50">
                Добавить
              </button>
            </div>
          </div>

          {channels.length > 0 ? (
            <div className="space-y-2">
              {channels.map((ch: ProjectChannel) => (
                <div key={ch.id} className="bg-white rounded-lg border p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">{ch.channel_title}</span>
                      {ch.channel_username && <span className="text-sm text-gray-400">@{ch.channel_username}</span>}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[ch.status] || 'bg-gray-100'}`}>
                        {STATUS_LABELS[ch.status] || ch.status}
                      </span>
                      {ch.seeding_score != null && (
                        <span className="text-xs text-gray-500">Score: {ch.seeding_score}</span>
                      )}
                      {ch.status === 'warming_up' && (
                        <span className="text-xs text-amber-600">{ch.warmup_count}/{ch.warmup_target}</span>
                      )}
                      {ch.status === 'cooldown' && ch.cooldown_until && (
                        <span className="text-xs text-gray-400">
                          до {new Date(ch.cooldown_until).toLocaleDateString('ru-RU')}
                        </span>
                      )}
                    </div>
                    {ch.members_count && (
                      <span className="text-xs text-gray-400">{ch.members_count.toLocaleString()} подп.</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {ch.status === 'added' && (
                      <button onClick={() => assessMutation.mutate(ch.id)}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Оценить</button>
                    )}
                    {ch.status === 'assessing' && (
                      <button onClick={() => viewAssessment(ch.id)}
                        className="px-2 py-1 bg-indigo-600 text-white text-xs rounded">Результат</button>
                    )}
                    {(ch.status === 'assessed' || ch.status === 'warming_up' || ch.status === 'warm') && (
                      <>
                        <button onClick={() => viewAssessment(ch.id)}
                          className="px-2 py-1 border text-xs rounded">Оценка</button>
                        <button onClick={() => generateMutation.mutate(ch.id)}
                          className="px-2 py-1 bg-primary-600 text-white text-xs rounded">
                          {ch.status === 'warm' ? 'Seed пост' : 'Warm-up'}
                        </button>
                      </>
                    )}
                    <button onClick={() => removeMutation.mutate(ch.id)}
                      className="px-2 py-1 text-red-500 text-xs hover:bg-red-50 rounded">×</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400">
              Добавьте каналы по @handle для начала работы
            </div>
          )}
        </div>
      )}

      {/* Tab: Posts */}
      {tab === 'posts' && (
        <div className="space-y-3">
          {posts.length > 0 ? posts.map((post: SeedPost) => {
            const ch = channels.find((c: ProjectChannel) => c.id === post.project_channel_id)
            return (
              <div key={post.id} className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    post.post_type === 'warmup' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                  }`}>{post.post_type}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    post.status === 'sent' ? 'bg-green-100 text-green-700' :
                    post.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                    post.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                  }`}>{post.status}</span>
                  {ch && <span className="text-xs text-gray-400">{ch.channel_title}</span>}
                </div>
                {post.reply_to_text && (
                  <div className="bg-gray-50 border-l-2 border-gray-300 pl-3 py-1 mb-2 text-xs text-gray-500">
                    В ответ на: {post.reply_to_text.slice(0, 150)}...
                  </div>
                )}
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{post.edited_content || post.content}</p>
                {post.status === 'draft' && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => approvePostMutation.mutate(post.id)}
                      className="px-3 py-1 bg-green-600 text-white text-xs rounded">Одобрить</button>
                  </div>
                )}
                {post.status === 'approved' && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => sendPostMutation.mutate(post.id)}
                      className="px-3 py-1 bg-primary-600 text-white text-xs rounded">Отправить</button>
                  </div>
                )}
              </div>
            )
          }) : (
            <div className="text-center py-10 text-gray-400">
              Нет постов. Генерируйте через кнопки на каналах.
            </div>
          )}
        </div>
      )}

      {/* Tab: Settings */}
      {tab === 'settings' && (
        <ProjectSettings project={project} onSave={() => queryClient.invalidateQueries({ queryKey: ['project', pId] })} />
      )}

      {/* Assessment modal */}
      {showPicker && (
        <ChannelPickerModal
          projectId={pId}
          onClose={() => setShowPicker(false)}
          onAdded={invalidate}
        />
      )}

      {selectedAssessment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedAssessment(null)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-3">Оценка канала</h3>
            <div className="space-y-3 text-sm">
              <div><strong>Тон:</strong> {selectedAssessment.tone_profile}</div>
              <div><strong>Язык:</strong> {selectedAssessment.language}</div>
              <div><strong>Темы:</strong> {selectedAssessment.topics}</div>
              <div><strong>Правила:</strong> {selectedAssessment.detected_rules}</div>
              <div className="flex gap-4">
                <span>Активность: <strong>{selectedAssessment.activity_score}</strong>/100</span>
                <span>Аудитория: <strong>{selectedAssessment.audience_quality}</strong>/100</span>
                <span>Сидинг: <strong>{selectedAssessment.seeding_score}</strong>/100</span>
              </div>
              <div><strong>Рекомендация:</strong> {selectedAssessment.recommendation}</div>
              <div className="bg-gray-50 p-3 rounded-lg">{selectedAssessment.ai_summary}</div>
              <div className="text-xs text-gray-400">Проанализировано сообщений: {selectedAssessment.messages_analyzed}</div>
            </div>
            {selectedAssessment.status === 'completed' && (
              <div className="flex gap-2 mt-4">
                <button onClick={() => approveAssessment.mutate(selectedAssessment.project_channel_id)}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg">Одобрить</button>
                <button onClick={() => rejectAssessment.mutate(selectedAssessment.project_channel_id)}
                  className="px-4 py-2 border text-sm rounded-lg">Отклонить</button>
              </div>
            )}
            <button onClick={() => setSelectedAssessment(null)}
              className="mt-3 text-xs text-gray-400 hover:text-gray-600">Закрыть</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Settings sub-component ───────────────────────────────────────

function ProjectSettings({ project, onSave }: { project: any; onSave: () => void }) {
  const [productInfo, setProductInfo] = useState(project.product_info || '')
  const [toneGuidelines, setToneGuidelines] = useState(project.tone_guidelines || '')
  const [targetAudience, setTargetAudience] = useState(project.target_audience || '')
  const [seedTemplates, setSeedTemplates] = useState(project.seed_templates || '')
  const [warmupPrompt, setWarmupPrompt] = useState(project.warmup_prompt || '')
  const [seedPrompt, setSeedPrompt] = useState(project.seed_prompt || '')

  const saveMutation = useMutation({
    mutationFn: () => projectsService.updateProject(project.id, {
      product_info: productInfo || undefined,
      tone_guidelines: toneGuidelines || undefined,
      target_audience: targetAudience || undefined,
      seed_templates: seedTemplates || undefined,
      warmup_prompt: warmupPrompt || undefined,
      seed_prompt: seedPrompt || undefined,
    }),
    onSuccess: () => { onSave(); toast.success('Сохранено') },
  })

  const fields = [
    { label: 'Информация о продукте', value: productInfo, set: setProductInfo, placeholder: 'eSIM сервис для путешественников, оплата криптой, на 40-80% дешевле Airalo' },
    { label: 'Целевая аудитория', value: targetAudience, set: setTargetAudience, placeholder: 'Digital nomads, крипто-трейдеры, экспаты' },
    { label: 'Тон общения', value: toneGuidelines, set: setToneGuidelines, placeholder: 'Неформальный, личная история, НЕ реклама' },
    { label: 'Промпт для warm-up (без продукта)', value: warmupPrompt, set: setWarmupPrompt, placeholder: 'Отвечай как эксперт по путешествиям...' },
    { label: 'Промпт для seed поста', value: seedPrompt, set: setSeedPrompt, placeholder: 'Пиши как личную историю...' },
    { label: 'Примеры seed постов (для AI reference)', value: seedTemplates, set: setSeedTemplates, placeholder: 'Вставь примеры хороших постов...' },
  ]

  return (
    <div className="space-y-4">
      {fields.map(f => (
        <div key={f.label}>
          <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
          <textarea value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder}
            rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
        </div>
      ))}
      <button onClick={() => saveMutation.mutate()}
        className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm">
        Сохранить настройки
      </button>
    </div>
  )
}
